// src/modules/inventory/components/InventoryTable.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import type { InventoryItem } from "../pages/InventoryListPage";

type Props = {
  items: InventoryItem[];
  loading?: boolean;
};

const InventoryTable: React.FC<Props> = ({ items, loading }) => {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto bg-white shadow rounded text-gray-800">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-3 py-2 text-gray-600">Name</th>
            <th className="px-3 py-2 text-gray-600">SKU</th>
            <th className="px-3 py-2 text-gray-600">Category</th>
            <th className="px-3 py-2 text-gray-600">Stock</th>
            <th className="px-3 py-2 text-gray-600">Reorder</th>
            <th className="px-3 py-2 text-gray-600">Supplier</th>
            <th className="px-3 py-2 text-gray-600">Price</th>
            <th className="px-3 py-2 text-gray-600">Status</th>
            <th className="px-3 py-2 text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const supplierName =
              typeof item.supplier === "string"
                ? item.supplier
                : typeof item.supplier === "object" && item.supplier !== null
                ? (item.supplier as any).name || "--"
                : "--";
            const isLow =
              typeof item.stock === "number" &&
              typeof item.reorderLevel === "number" &&
              item.stock <= item.reorderLevel;

            return (
              <tr key={item._id} className="border-b border-gray-200">
                <td className="px-3 py-2 font-medium">{item.name}</td>
                <td className="px-3 py-2">{item.sku}</td>
                <td className="px-3 py-2">{item.category}</td>
                <td className="px-3 py-2">
                  {isLow ? (
                    <span className="text-red-700 font-semibold">
                      {item.stock}
                      <span className="bg-red-100 text-red-600 px-2 py-0.5 ml-2 rounded text-xs">
                        Low
                      </span>
                    </span>
                  ) : (
                    item.stock ?? "--"
                  )}
                </td>
                <td className="px-3 py-2">{item.reorderLevel ?? "--"}</td>
                <td className="px-3 py-2">{supplierName}</td>
                <td className="px-3 py-2">
                  {item.price != null && !isNaN(Number(item.price))
                    ? `$${Number(item.price).toFixed(2)}`
                    : "--"}
                </td>
                <td className="px-3 py-2">
                  {item.status === "discontinued" ? (
                    <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs">
                      Discontinued
                    </span>
                  ) : (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                      Active
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <button
                    className="text-blue-700 underline mr-2"
                    onClick={() => navigate(`/inventory/${item._id}`)}
                  >
                    View
                  </button>
                  <button
                    className="text-green-700 underline"
                    onClick={() => navigate(`/inventory/${item._id}/edit`)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            );
          })}

          {!items.length && !loading && (
            <tr>
              <td colSpan={9} className="text-center text-gray-400 py-8">
                No inventory items found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {loading && (
        <div className="text-center py-6 text-blue-700">Loading...</div>
      )}
    </div>
  );
};

export default InventoryTable;
